/**
 * Global hardware laser wedge for Zebra / Android Chrome (mobile web).
 * Only intercepts FAST key bursts (scanner). Normal typing passes through unchanged.
 */

import { Platform } from "react-native";
import { shouldUseHardwareWedge } from "./mobileWeb";

const SCAN_GAP_MS = 80;
const IDLE_COMMIT_MS = 350;
const GUARD_MS = 800;
const DEDUPE_MS = 30;

/** Set false to disable scan debug popups on Zebra tablets. */
const WEDGE_DEBUG_ALERT = true;

let installed = false;
let committing = false;
let buffer = "";
let lastKeyAt = 0;
let capturing = false;
let pendingTarget = null;
let idleTimer = null;
let commitTimer = null;
let lastCommitAt = 0;
let lastAppendAt = 0;
let lastAppendChunk = "";
let lastAppendSource = "";
let domObserver = null;
let focusedTarget = null;

function active() {
  return Platform.OS === "web" && shouldUseHardwareWedge();
}

function clearTimers() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  if (commitTimer) {
    clearTimeout(commitTimer);
    commitTimer = null;
  }
}

function resetCapture() {
  buffer = "";
  capturing = false;
  pendingTarget = null;
  clearTimers();
}

function resolveEditableTarget(fromEl) {
  let el = fromEl || focusedTarget || (typeof document !== "undefined" ? document.activeElement : null);
  if (!el) return null;

  for (let i = 0; i < 6 && el; i++) {
    const tag = (el.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA") {
      if (el.disabled || el.readOnly) return null;
      const type = (el.type || "").toLowerCase();
      if (type === "password" || type === "file" || type === "hidden") return null;
      return el;
    }
    if (el.querySelector) {
      const nested = el.querySelector(
        "input:not([disabled]):not([readonly]):not([type=password]):not([type=file]):not([type=hidden]), textarea:not([disabled]):not([readonly])"
      );
      if (nested) {
        el = nested;
        continue;
      }
    }
    break;
  }
  return null;
}

function cleanText(raw) {
  if (raw == null) return "";
  return String(raw).replace(/[\x00-\x1F\x7F]/g, "");
}

function showWedgeDebugAlert({ raw, clean, valueBefore, valueAfter, source, target }) {
  if (!WEDGE_DEBUG_ALERT || typeof window === "undefined" || !window.alert) return;

  const tag = target ? (target.tagName || "").toLowerCase() : "?";
  const fieldHint =
    target &&
    (target.placeholder || target.name || target.id || target.getAttribute("aria-label") || "");
  const lines = [
    `Source: ${source}`,
    `Raw length: ${raw.length}`,
    `Raw: ${JSON.stringify(raw)}`,
    `Clean length: ${clean.length}`,
    `Clean: ${JSON.stringify(clean)}`,
    `Input before: ${JSON.stringify(valueBefore)}`,
    `Input after: ${JSON.stringify(valueAfter)}`,
    `Field: <${tag}>${fieldHint ? ` "${fieldHint}"` : ""}`,
  ];

  window.alert(`[Zebra scan debug]\n\n${lines.join("\n")}`);
}

function stopEvent(e) {
  e.preventDefault();
  e.stopImmediatePropagation();
  if (e.stopPropagation) e.stopPropagation();
}

function getReactProps(node) {
  if (!node) return null;
  for (const key of Object.keys(node)) {
    if (key.startsWith("__reactProps$")) return node[key];
  }
  return null;
}

function setDomValue(element, value) {
  const tag = (element.tagName || "").toUpperCase();
  const proto = tag === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const prev = element.value;
  const tracker = element._valueTracker;
  if (tracker) tracker.setValue(prev);
  const desc = Object.getOwnPropertyDescriptor(proto, "value");
  if (desc && desc.set) desc.set.call(element, value);
  else element.value = value;
}

function commitValueToInput(element, value, { triggerSubmit = false } = {}) {
  if (!element) return;

  committing = true;
  try {
    setDomValue(element, value);

    const props = getReactProps(element);
    if (props && typeof props.onChange === "function") {
      props.onChange({
        target: element,
        currentTarget: element,
        nativeEvent: { text: value },
      });
    } else if (typeof InputEvent === "function") {
      element.dispatchEvent(
        new InputEvent("input", { bubbles: true, inputType: "insertText", data: value })
      );
    } else {
      element.dispatchEvent(new Event("input", { bubbles: true }));
    }

    if (triggerSubmit && props && typeof props.onKeyDown === "function") {
      props.onKeyDown({
        key: "Enter",
        code: "Enter",
        keyCode: 13,
        which: 13,
        shiftKey: false,
        target: element,
        currentTarget: element,
        nativeEvent: { text: value, key: "Enter" },
        preventDefault: () => {},
        stopPropagation: () => {},
        isDefaultPrevented: () => true,
      });
    }
  } finally {
    committing = false;
  }
}

function commitNow(target, raw, source = "commit") {
  const clean = cleanText(raw);
  if (!clean || !target) {
    resetCapture();
    return;
  }
  const valueBefore = target.value || "";
  try {
    commitValueToInput(target, clean, { triggerSubmit: true });
    lastCommitAt = Date.now();
    showWedgeDebugAlert({
      raw,
      clean,
      valueBefore,
      valueAfter: target.value || "",
      source,
      target,
    });
  } catch (err) {
    console.warn("[wedge] commit failed", err);
    if (WEDGE_DEBUG_ALERT && typeof window !== "undefined" && window.alert) {
      window.alert(`[Zebra scan debug] commit failed\n\n${String(err)}`);
    }
  }
  resetCapture();
}

function scheduleCommit(target, raw, source = "scheduled") {
  if (commitTimer) clearTimeout(commitTimer);
  commitTimer = setTimeout(() => {
    commitTimer = null;
    commitNow(target, raw, source);
  }, 30);
}

function scheduleIdleCommit() {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (capturing && buffer && pendingTarget) {
      scheduleCommit(pendingTarget, buffer, "idle-timeout");
    } else {
      resetCapture();
    }
  }, IDLE_COMMIT_MS);
}

function shouldDedupe(source, chunk) {
  const now = Date.now();
  if (
    source !== lastAppendSource &&
    chunk === lastAppendChunk &&
    now - lastAppendAt < DEDUPE_MS
  ) {
    return true;
  }
  lastAppendAt = now;
  lastAppendChunk = chunk;
  lastAppendSource = source;
  return false;
}

function beginCapture(target) {
  capturing = true;
  pendingTarget = target;
  buffer = cleanText(target.value || "");
}

function appendToBuffer(target, text, source) {
  if (!text || shouldDedupe(source, text)) return;
  const chunk = String(text);
  if (!buffer.endsWith(chunk)) {
    buffer += chunk;
  }
  pendingTarget = target;
  scheduleIdleCommit();
}

function handlePrintableKey(target, text, source, e) {
  const now = Date.now();
  const gap = lastKeyAt ? now - lastKeyAt : SCAN_GAP_MS + 1;
  lastKeyAt = now;

  if (capturing) {
    stopEvent(e);
    appendToBuffer(target, text, source);
    return;
  }

  if (gap < SCAN_GAP_MS) {
    stopEvent(e);
    beginCapture(target);
    appendToBuffer(target, text, source);
    return;
  }

  /* Slow human typing — do not intercept. */
}

function finishScan(target, e) {
  if (capturing && buffer.length > 0) {
    if (e) stopEvent(e);
    const data = buffer;
    const t = pendingTarget || target;
    clearTimers();
    buffer = "";
    capturing = false;
    pendingTarget = null;
    scheduleCommit(t, data, "terminator");
    return true;
  }
  if (capturing) resetCapture();
  return false;
}

function isTerminator(e) {
  return (
    e.key === "Enter" ||
    e.key === "Tab" ||
    e.key === "Return" ||
    e.keyCode === 13 ||
    e.keyCode === 9
  );
}

function isTerminatorData(data) {
  return data === "\n" || data === "\r" || data === "\t";
}

function handleBeforeInput(e) {
  if (!active() || committing) return;
  const target = resolveEditableTarget(e.target);
  if (!target) return;

  const type = e.inputType || "";
  const data = e.data;

  if (
    type === "insertLineBreak" ||
    type === "insertParagraph" ||
    (data && isTerminatorData(data))
  ) {
    if (capturing) {
      stopEvent(e);
      finishScan(target, e);
    }
    return;
  }

  if (type === "insertText" && data && !isTerminatorData(data)) {
    handlePrintableKey(target, data, "beforeinput", e);
  }
}

function handleKeyDown(e) {
  if (!active() || committing) return;
  const target = resolveEditableTarget(document.activeElement);
  if (!target) {
    if (capturing) resetCapture();
    return;
  }

  if (isTerminator(e)) {
    if (capturing) {
      stopEvent(e);
      finishScan(target, e);
    } else if (Date.now() - lastCommitAt < GUARD_MS) {
      stopEvent(e);
    }
    return;
  }

  if (e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    handlePrintableKey(target, e.key, "keydown", e);
  }
}

function handleKeyPress(e) {
  if (!active() || !capturing) return;
  if (e.keyCode !== 13 && e.keyCode !== 9) return;
  stopEvent(e);
}

function handleSubmit(e) {
  if (!active()) return;
  if (capturing || Date.now() - lastCommitAt < GUARD_MS) {
    stopEvent(e);
  }
}

function handleFocusIn(e) {
  focusedTarget = resolveEditableTarget(e.target);
}

function handleFocusOut() {
  if (!capturing) {
    focusedTarget = null;
    lastKeyAt = 0;
  }
}

function patchInputElement(input) {
  if (!input || (input.tagName || "").toUpperCase() !== "INPUT") return;
  try {
    if (input.type === "search") input.type = "text";
    input.setAttribute("enterkeyhint", "done");
    input.setAttribute("inputmode", "text");
    input.setAttribute("autocomplete", "off");
  } catch (_) {}
}

function patchInputs(root) {
  if (typeof document === "undefined") return;
  try {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll("input, textarea").forEach((el) => {
      if ((el.tagName || "").toUpperCase() === "INPUT") patchInputElement(el);
    });
  } catch (_) {}
}

function startInputPatchObserver() {
  if (domObserver || typeof MutationObserver === "undefined" || !document.body) return;
  patchInputs(document);
  domObserver = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === "INPUT") patchInputElement(node);
        else patchInputs(node);
      });
    });
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
}

function attachListeners() {
  window.addEventListener("keydown", handleKeyDown, true);
  window.addEventListener("keypress", handleKeyPress, true);
  document.addEventListener("beforeinput", handleBeforeInput, true);
  document.addEventListener("submit", handleSubmit, true);
  document.addEventListener("focusin", handleFocusIn, true);
  document.addEventListener("focusout", handleFocusOut, true);
}

function detachListeners() {
  window.removeEventListener("keydown", handleKeyDown, true);
  window.removeEventListener("keypress", handleKeyPress, true);
  document.removeEventListener("beforeinput", handleBeforeInput, true);
  document.removeEventListener("submit", handleSubmit, true);
  document.removeEventListener("focusin", handleFocusIn, true);
  document.removeEventListener("focusout", handleFocusOut, true);
}

export function installGlobalHardwareWedge() {
  if (installed || Platform.OS !== "web" || typeof window === "undefined") return () => {};
  if (!shouldUseHardwareWedge()) return () => {};

  installed = true;
  attachListeners();

  const boot = () => {
    patchInputs(document);
    startInputPatchObserver();
  };
  if (document.body) boot();
  else document.addEventListener("DOMContentLoaded", boot);

  return uninstallGlobalHardwareWedge;
}

export function uninstallGlobalHardwareWedge() {
  if (!installed) return;
  detachListeners();
  if (domObserver) {
    domObserver.disconnect();
    domObserver = null;
  }
  resetCapture();
  focusedTarget = null;
  installed = false;
}

if (Platform.OS === "web" && typeof window !== "undefined") {
  try {
    installGlobalHardwareWedge();
  } catch (_) {}
}
